import { createClient } from "@connectrpc/connect";
import { ISaveService } from "../../../abstractions/services/actions/ISaveService";
import { serviceProvider } from "../../../comun/provider/ServiceProvider";
import { grpcTransportProvider } from "../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../comun/services/grpc/mapConnectError";
import { ClienteFincaLoteService } from "../../../generated/grpc/terceros_lote_pb";
import {
  fromClienteFincaBaseResponse,
  grpcFincaLoteDatabase,
  toClienteFincaBaseResponse,
} from "../database/GrpcFincaLoteDatabase";
import { grpcLoteDatabase } from "../database/GrpcLoteDatabase";
import { ClienteFincaLoteRequest } from "../request/ClienteFincaLoteRequest";
import { ClienteFincaLoteResponse } from "../response/ClienteFincaLoteResponse";
import { fx_createLoteGrpcService, toClienteFincaLoteResponse } from "./LoteGrpcService";

/** Variante gRPC-Web de `LoteSaveService` (ver ese archivo): mismo contrato
 *  `ISaveService`, mismo guardado de solo actualización (nombre, cultivo,
 *  área productiva). Tras guardar, `putOneAsync` deja el lote actualizado en
 *  `grpcLoteDatabase` y se fuerza la recarga completa de los lotes de la finca
 *  (`recargarTodoAsync`), como en todo guardado que cambia un lote. El server
 *  también recalcula las hectáreas productivas de la finca (2026-10-08) y la
 *  app las deja en la finca guardada y en la seleccionada. */
export class LoteSaveGrpcService
  implements ISaveService<ClienteFincaLoteRequest, ClienteFincaLoteResponse>
{
  async saveAsync(
    request: ClienteFincaLoteRequest,
  ): Promise<ClienteFincaLoteResponse> {
    const client = createClient(ClienteFincaLoteService, grpcTransportProvider.get());

    let respuesta;
    try {
      respuesta = await client.guardar({
        id: request.id,
        nombreLote: request.nombreLote,
        isAreaProductiva: request.isAreaProductiva,
        cultivoId: request.cultivoId ?? undefined,
      });
    } catch (error) {
      throw mapConnectError(error);
    }

    const saved = respuesta.lote;
    if (!saved) {
      throw new Error("El servidor no devolvió el lote guardado.");
    }

    await grpcLoteDatabase.putOneAsync(saved);
    await fx_createLoteGrpcService(saved.fincaId).recargarTodoAsync();
    if (respuesta.fincaHectareasProductivas !== undefined) {
      await fx_actualizarHectareasProductivasAsync(
        saved.fincaId,
        respuesta.fincaHectareasProductivas,
      );
    }
    return toClienteFincaLoteResponse(saved);
  }
}

/** Deja las hectáreas productivas que recalculó el server en la finca guardada
 *  del login y, si es la finca seleccionada, también en ella (2026-10-08). De
 *  ahí las leen la capacidad del dashboard de ganadería y el "Área productiva"
 *  del menú, que se vuelve a pintar al cambiar la finca seleccionada. Antes
 *  quedaban con el valor anterior al marcar o desmarcar un lote como área
 *  productiva. */
async function fx_actualizarHectareasProductivasAsync(
  fincaId: string,
  hectareasProductivas: number,
): Promise<void> {
  const fincaGuardada = (await grpcFincaLoteDatabase.getFincasAsync()).find(
    (finca) => finca.id === fincaId,
  );
  if (fincaGuardada) {
    await grpcFincaLoteDatabase.upsertFincaAsync(
      fromClienteFincaBaseResponse({
        ...toClienteFincaBaseResponse(fincaGuardada),
        hectareasProductivas,
      }),
    );
  }

  const fincaSeleccionada = serviceProvider.getFincaSelectedService().get();
  if (fincaSeleccionada?.id === fincaId) {
    serviceProvider
      .getFincaSelectedService()
      .set({ ...fincaSeleccionada, hectareasProductivas });
  }
}
